import React from "react";

// Components
import SubtleButton from "../../../../../components/Buttons/SubtleButton";
import OverCreditBanner from "../../../../../components/Paywall/OverCreditBanner";
import PaywallProvider, {
  usePaywall,
} from "../../../../../components/SiteMap/contexts/PaywallContext";
import Sidepanel from "../../../../../components/Maps/ActiveRoutes/ui/sidepanel";
import { EditableJobsForRoute } from "../EditableJobItem";
import SidepanelFooter from "../SidepanelFooter";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { MapMarker, StartEndMapMarker } from "../Map/Marker";
import { useContext } from "react";
import { RouteContext } from "../RouteContext";
import { useState } from "react";
import { HeaderWithDetails } from "./Components/HeaderWithDetails";
import { drivingDetails } from "./utils";
import RoutePath from "../../../../../components/Maps/ActiveRoutes/RoutePath";
import { LocationSwitcher } from "./Index/LocationSwitcher";
import { useMapJobsZoom } from "../../../../../components/Maps/ActiveRoutes/use-map-zoom";
import { resolveRouteColorPalette } from "../../../../../components/Maps/ActiveRoutes/routeColor";
import { LocationChooser } from "./Components/LocationChooser";
import SparkAiIcon from "../../../../../resources/sparkai.svg";

const OUTDATED_ROUTE_COLOR = "#555";

const IndexRoute = () => (
  <PaywallProvider>
    <IndexRouteInner />
  </PaywallProvider>
);

const IndexRouteInner = () => {
  let navigate = useNavigate();
  let { routeId } = useParams();

  let [highlightJobId, setHighlightJobId] = useState(null);
  let [showLocations, setShowLocations] = useState(false);

  // pull jobs for this route of route context
  const {
    companyLocations,
    route,
    routeColor,
    jobs,
    routeChanged,
    routeLocations,
    setJobs,
    setRouteLocation,
    saveNewLocation,
  } = useContext(RouteContext);

  const resetZoom = useMapJobsZoom(jobs);

  let addRouteClick = (e) => {
    e.preventDefault();
    navigate(`/admin/route/${routeId}/main/add-jobs`, {
      preventScrollReset: true,
    });
  };
  let previewClick = (e) => {
    e.preventDefault();
    navigate(`/admin/route/${routeId}/preview`, {
      preventScrollReset: true,
    });
  };
  let optimizeJobsClick = (e) => {
    e.preventDefault();
    navigate(`/admin/route/${routeId}/optimize`, {
      preventScrollReset: true,
    });
  };
  const updateOrder = (reorderedJobs, movedJobId) => {
    setJobs(reorderedJobs);
    setHighlightJobId(movedJobId);
  };
  const onDelete = (jobId) => {
    setJobs(jobs.filter((job) => job.id !== jobId));
  };
  const onClick = (jobId) => {
    setHighlightJobId(highlightJobId === jobId ? null : jobId);
    resetZoom();
  };

  const addLocation = (type, place) => {
    setRouteLocation(type, {
      _id: "",
      address: place.formatted_address,
      location: place.geometry.location.toJSON(),
      name: place.name,
    });
  };

  let drivingInfo =
    routeChanged ||
    !route?.drivingDistanceFormatted ||
    !route?.drivingDurationSeconds
      ? ""
      : drivingDetails(
          route.drivingDistanceFormatted,
          route.drivingDurationSeconds,
        );

  const haveJobs = jobs.length > 0;
  const routePathColor = resolveRouteColorPalette(routeColor).hex;

  const { routeOptimizationCreditsUsed, routeOptimizationCreditsWarning } =
    usePaywall();

  const disableBtns =
    routeOptimizationCreditsUsed ||
    !haveJobs ||
    jobs.length < 2 ||
    !routeLocations.start.location?.lat ||
    !routeLocations.end.location?.lat;

  return (
    <>
      <Sidepanel>
        <div className="h-full grid grid-rows-[fit-content(140px)_auto_77px]">
          <HeaderWithDetails
            isLoading={false}
            routeChanged={routeChanged}
            routeColor={routeColor}
            routeDetails={drivingInfo}
            routeName={route.name}
          />

          <div className="px-5 py-4 md-py-16 relative overflow-y-scroll">
            {/* We pass in a method so we can further configure our address chooser */}
            <EditableJobsForRoute
              locationChooser={(type) => (
                <LocationChooser
                  locations={companyLocations}
                  routeLocations={routeLocations}
                  onAddLocation={addLocation}
                  onChooseLocation={setRouteLocation}
                  onSaveLocation={saveNewLocation}
                  type={type}
                />
              )}
              jobs={jobs}
              highlightJobId={highlightJobId}
              color={routeColor}
              routeLocations={routeLocations}
              showLocations={showLocations}
              updateOrder={updateOrder}
              onAdd={addRouteClick}
              onClick={onClick}
              onDelete={onDelete}
            />
            {haveJobs ? (
              <div className="mt-0 pt-4 text-left">
                <button
                  onClick={addRouteClick}
                  type="submit"
                  className="text-xs px-2 py-1 rounded font-bold border border-solid border-gray-200 bg-white hover:bg-gray-50 text-gray-500"
                  data-testid="add-properties-button"
                >
                  + Add a Property
                </button>
              </div>
            ) : null}
            <LocationSwitcher
              onAdd={addRouteClick}
              showLocations={showLocations}
              toggleShowLocations={() => setShowLocations(!showLocations)}
            />
          </div>

          <RoutePath
            path={route.drivingPath}
            color={!routeChanged ? routePathColor : OUTDATED_ROUTE_COLOR}
          />

          {routeLocations?.start?.location?.lat ? (
            <StartEndMapMarker
              type="start"
              position={routeLocations.start.location}
            />
          ) : null}
          {routeLocations?.end?.location?.lat ? (
            <StartEndMapMarker
              type="end"
              position={routeLocations.end.location}
            />
          ) : null}

          {jobs.map((job, index) => {
            if (!job.location.lat || !job.location.lng) return null;

            return (
              <MapMarker
                key={job.id}
                color={routeColor}
                number={index + 1}
                highlight={job.id === highlightJobId}
                isNew={job.isNew ?? false}
                onClick={() => {
                  setHighlightJobId(job.id);
                }}
                position={{ lat: job.location.lat, lng: job.location.lng }}
              />
            );
          })}

          {(routeOptimizationCreditsUsed ||
            routeOptimizationCreditsWarning) && (
            <div className="absolute bottom-[77px] left-0 right-0">
              {routeOptimizationCreditsUsed ? (
                <OverCreditBanner
                  linkTo="/admin/settings/billing?show-plans=1"
                  linkText={routeOptimizationCreditsWarning}
                />
              ) : (
                <OverCreditBanner
                  linkTo="/admin/settings/billing?show-plans=1"
                  linkText="Optimizing credits used, upgrade now"
                />
              )}
            </div>
          )}
          <SidepanelFooter>
            <SubtleButton
              onClick={optimizeJobsClick}
              type="submit"
              testId="optimize-properties"
              buttonRole="primary"
              disabled={disableBtns}
              className="inline-flex justify-center rounded-md px-3 py-2 text-sm"
            >
              <img src={SparkAiIcon} alt="" className="size-4" />
              &nbsp;Optimize ...
            </SubtleButton>

            <SubtleButton
              onClick={previewClick}
              type="submit"
              testId="add-properties-button"
              buttonRole="secondary"
              disabled={disableBtns}
              className="inline-flex justify-center rounded-md px-3 py-2 text-sm"
            >
              Preview
            </SubtleButton>
          </SidepanelFooter>
        </div>
      </Sidepanel>
      <Outlet />
    </>
  );
};

export default IndexRoute;
